import { useState } from "react";
import { X, Compass, Trophy, ShieldCheck, Info, ArrowRight, HandCoins, Sparkles } from "lucide-react";
import { SPRINT_FIRST_STAKE, SPRINT_FIRST_DAYS, SPRINT_PLATFORM_FEE_PCT, EXPLORATION_DAYS } from "@/lib/partnershipLifecycle";

/**
 * Rules modal shown before a user starts a partnership.
 * The initial seven-day Starter Sprint is always available; paid stake sprints are optional.
 */
export function PartnershipRulesModal({ open, onClose, onConfirm, partnerName, stakesEnabled = false, trialDays = 7 }) {
  const [acknowledged, setAcknowledged] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="partnership-rules-modal">
      <div className="hm-card !bg-[#0d0d0d] max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-md relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#F5F5F0]" data-testid="rules-close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-8 sm:p-10">
          <div className="overline mb-3">Before you start</div>
          <h2 className="font-serif-editorial font-light tracking-tighter text-3xl sm:text-4xl leading-[1.05]">
            How partnerships work on <em className="italic text-[#F59E0B]">HustlrMate.</em>
          </h2>
          <p className="mt-3 text-[#A1A1AA] font-light">
            You're about to team up with <span className="text-[#F5F5F0]">{partnerName || "your match"}</span>. Your shared workspace opens as soon as you confirm.
          </p>

          {/* Chapter 1 */}
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30">
                <Compass className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">Starter Sprint</div>
                <div className="font-serif-editorial text-xl">Your first {trialDays} days together</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#F5F5F0] font-light pl-2">
              <Bullet>Both people must confirm before the Starter Sprint clock begins.</Bullet>
              <Bullet>Use the complete shared workspace to check in, plan realistic tasks, test communication, and learn each other's working style.</Bullet>
              <Bullet>Start steadily. The goal is to establish a rhythm you can honestly sustain, not perform for seven days and burn out.</Bullet>
              <Bullet>At the end, both of you must choose to continue. One “no” ends the partnership, and ending it does not restore either person's match credit.</Bullet>
              <Bullet>After the Starter Sprint, normal Free or paid workspace access applies.</Bullet>
            </ul>
          </div>

          {/* Chapter 2 */}
          {stakesEnabled && <div className="mt-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30">
                <Trophy className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">Phase 2 · if you both say yes</div>
                <div className="font-serif-editorial text-xl">The Sprint Pact — {SPRINT_FIRST_DAYS} days</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#F5F5F0] font-light pl-2">
              <Bullet>Both of you lock <span className="text-[#F5F5F0]">{SPRINT_FIRST_STAKE} credits</span> into a shared stake for your first sprint.</Bullet>
              <Bullet>Every missed task shifts a proportional share of your stake to your partner. Consistency = you keep your money.</Bullet>
              <Bullet>HustlrMate takes a small <span className="text-[#F5F5F0]">{SPRINT_PLATFORM_FEE_PCT}%</span> platform fee on transferred amounts. That's it.</Bullet>
              <Bullet>Later sprints, you two decide the length and the amount together — go big if you're ready.</Bullet>
            </ul>
          </div>}

          <div className="mt-8 p-4 rounded-md border border-[#F59E0B]/20 bg-[#F59E0B]/[0.03] flex gap-3">
            <Info className="w-4 h-4 mt-0.5 text-[#F59E0B] shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-[#A1A1AA] font-light">
              {stakesEnabled
                ? <>Money isn't the point — the <span className="text-[#F5F5F0]">social contract</span> is. Real stakes make skipping loud instead of silent.</>
                : <>These seven days protect both people from committing blindly. Use them to test reliability, communication, and whether your routines genuinely fit.</>}
            </p>
          </div>

          <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="rules-acknowledge">
            <input
              type="checkbox" checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 accent-[#F59E0B]"
            />
            <span className="text-sm text-[#F5F5F0] font-light">
               I understand the seven-day Starter Sprint and that ending afterward does not return a match credit.
            </span>
          </label>

          <div className="mt-6 flex items-center gap-3">
            <button onClick={onClose} className="hm-btn-ghost px-5 py-2.5 rounded-md text-sm">Not yet</button>
            <button
              onClick={onConfirm}
              disabled={!acknowledged}
              data-testid="rules-confirm"
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold transition-all ${
                acknowledged ? "hm-btn-primary" : "bg-white/10 text-[#7a7a80] cursor-not-allowed"
              }`}
            >
              Start workspace <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="text-[#F59E0B] mt-1">◆</span>
      <span className="text-[#A1A1AA]">{children}</span>
    </li>
  );
}

/**
 * Phase banner shown at top of Workspace.
 */
export function PhaseBanner({ phase, dayNumber, daysLeft, partnerName, sprint }) {
  if (phase === "exploration") {
    return (
      <Banner tone="soft" icon={Compass} title={`Starter Sprint · Day ${dayNumber} of ${EXPLORATION_DAYS}`} testId="phase-banner-exploration">
        <p>Use this week deliberately: plan achievable work, complete your tasks, communicate early, and learn how {partnerName} handles accountability. Build a sustainable rhythm before making a longer commitment.</p>
        <ProgressBar current={dayNumber} total={EXPLORATION_DAYS} />
        <p className="text-xs text-[#7a7a80] mt-3">After seven days, you must both choose to continue. One “no” ends the partnership without returning either match credit. {daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} to go.` : ""}</p>
      </Banner>
    );
  }
  if (phase === "sprint") {
    const stake = sprint?.stakeTokens || 0;
    return (
      <Banner tone="loud" icon={Trophy} title={`Sprint ${sprint?.number || 1} · Day ${dayNumber} of ${sprint?.lengthDays || 21}`} testId="phase-banner-sprint">
        <p>
          <span className="text-[#F59E0B] font-serif-editorial text-lg">{stake} credits</span> each on the line. Every missed task moves a share of your stake to your partner.
        </p>
        <ProgressBar current={dayNumber} total={sprint?.lengthDays || 21} />
        <p className="text-xs text-[#7a7a80] mt-3">{daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "Sprint complete"}. Show up.</p>
      </Banner>
    );
  }
  return null;
}
function Banner({ icon: Icon, title, children, tone = "soft", testId }) {
  const toneCls = tone === "loud"
    ? "border-[#F59E0B]/40 bg-gradient-to-br from-[#F59E0B]/[0.06] to-transparent"
    : "border-white/10 bg-white/[0.02]";
  return (
    <div className={`hm-card p-5 sm:p-6 rounded-md mb-6 border ${toneCls}`} data-testid={testId}>
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-serif-editorial text-xl mb-2">{title}</div>
          <div className="text-sm text-[#A1A1AA] font-light space-y-2">{children}</div>
        </div>
      </div>
    </div>
  );
}
function ProgressBar({ current, total }) {
  const pct = Math.max(0, Math.min(100, (current / total) * 100));
  return (
    <div className="mt-3">
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-[#F59E0B] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Day-7 decision panel — go/no-go with optional message.
 */
export function DecisionPanel({ partnership, partnerName, onDecide, stakesEnabled = false }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const ex = partnership.explorationDecision || {};
  const myAnswer = ex.me;
  const theirAnswer = ex.them;

  const submit = (dec) => {
    setBusy(true);
    onDecide(dec, msg);
    setTimeout(() => setBusy(false), 500);
  };

  return (
    <div className="hm-card p-6 sm:p-8 rounded-md mb-6 border border-[#F59E0B]/30" data-testid="decision-panel">
      <div className="overline mb-3">Starter Sprint complete · Decision time</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-tight mb-3">
        Seven days are complete. <em className="italic text-[#F59E0B]">Do you continue?</em>
      </h2>
      <p className="text-sm text-[#A1A1AA] font-light max-w-2xl mb-6">
        You've tested the working rhythm with {partnerName}. Was communication reliable and the effort compatible? {stakesEnabled ? "If you both agree, you can set up the next Sprint Pact." : "If you both agree, the partnership continues normally."} If either person says no, the partnership ends and neither match credit is restored.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <StatusChip who="you" value={myAnswer} />
        <StatusChip who={partnerName} value={theirAnswer} />
      </div>

      {myAnswer == null ? (
        <>
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Leave a short note (optional — shown to your partner if you end it)</div>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Only used if you decide to end the partnership."
              rows={2}
              className="hm-input w-full px-3 py-2.5 rounded-md text-sm font-light resize-none"
              data-testid="decision-message"
              maxLength={280}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => submit("yes")}
              disabled={busy}
              data-testid="decision-yes"
              className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold"
            >
              <Sparkles className="w-4 h-4" /> {stakesEnabled ? "Yes — continue to Sprint Pact" : "Yes — continue this partnership"}
            </button>
            <button
              onClick={() => submit("no")}
              disabled={busy}
              data-testid="decision-no"
              className="hm-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm"
            >
              No — end the partnership
            </button>
          </div>
        </>
      ) : (
        <div className="p-4 rounded-md border border-white/5 bg-white/[0.02] text-sm text-[#A1A1AA] font-light">
          {myAnswer === "yes" && theirAnswer == null && <>You said <span className="text-[#F5F5F0]">yes</span>. Waiting on {partnerName}…</>}
          {myAnswer === "yes" && theirAnswer === "yes" && <>{stakesEnabled ? "You both said yes. Moving to Sprint Pact setup below." : "You both said yes. Your partnership continues."}</>}
          {myAnswer === "no" && <>You ended the partnership. The match credit remains used.</>}
        </div>
      )}
    </div>
  );
}
function StatusChip({ who, value }) {
  const label = value === "yes" ? "Yes — continue" : value === "no" ? "No — end" : "Waiting…";
  const cls = value === "yes"
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
    : value === "no"
    ? "border-red-500/40 bg-red-500/10 text-red-300"
    : "border-white/10 bg-white/[0.02] text-[#A1A1AA]";
  return (
    <div className={`px-4 py-3 rounded-md border ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{who}</div>
      <div className="text-sm font-medium mt-0.5">{label}</div>
    </div>
  );
}

/**
 * Sprint setup panel — both said yes, now lock stakes.
 */
export function SprintSetupPanel({ partnership, partnerName, onStart }) {
  const isFirst = (partnership.sprintHistory || []).length === 0;
  const [lengthDays, setLengthDays] = useState(isFirst ? SPRINT_FIRST_DAYS : 21);
  const [stake, setStake] = useState(isFirst ? SPRINT_FIRST_STAKE : 250);
  const [ack, setAck] = useState(false);

  return (
    <div className="hm-card p-6 sm:p-8 rounded-md mb-6 border border-[#F59E0B]/30" data-testid="sprint-setup-panel">
      <div className="overline mb-3">Sprint Pact · setup</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-tight mb-3">
        Time to put <em className="italic text-[#F59E0B]">skin in the game.</em>
      </h2>
      <p className="text-sm text-[#A1A1AA] font-light max-w-2xl mb-6">
        {isFirst
          ? <>Your first Sprint Pact is fixed — <span className="text-[#F5F5F0]">{SPRINT_FIRST_STAKE} credits</span> each, <span className="text-[#F5F5F0]">{SPRINT_FIRST_DAYS} days</span>. Enough to sting, not enough to hurt. Nail this, and next time you two decide the length and the amount.</>
          : <>You and {partnerName} have completed a sprint before. Set the length and the stake together for this one.</>}
      </p>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Length">
          <input
            type="number" min={7} max={90} value={lengthDays}
            disabled={isFirst}
            onChange={(e) => setLengthDays(Number(e.target.value))}
            className="hm-input w-full px-3 py-2 rounded-md text-sm"
            data-testid="sprint-length"
          />
          <div className="text-[10px] text-[#7a7a80] mt-1">days</div>
        </FieldCard>
        <FieldCard label="Stake per person">
          <div className="flex items-center gap-1">
            <input
              type="number" min={10} max={5000} step={10} value={stake}
              disabled={isFirst}
              onChange={(e) => setStake(Number(e.target.value))}
              className="hm-input w-full px-3 py-2 rounded-md text-sm"
              data-testid="sprint-stake"
            />
          </div>
          <div className="text-[10px] text-[#7a7a80] mt-1">credits · total pot {stake * 2}</div>
        </FieldCard>
        <FieldCard label="Platform fee">
          <div className="text-sm text-[#F5F5F0]">{SPRINT_PLATFORM_FEE_PCT}%</div>
          <div className="text-[10px] text-[#7a7a80] mt-1">Only on transferred amounts</div>
        </FieldCard>
      </div>

      <div className="p-4 rounded-md border border-white/5 bg-white/[0.02] mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">How settlement works</div>
        <ul className="text-sm text-[#A1A1AA] font-light space-y-1.5">
          <li className="flex gap-2"><span className="text-[#F59E0B]">◆</span> Complete every scheduled task in this sprint → you keep all {stake} credits.</li>
          <li className="flex gap-2"><span className="text-[#F59E0B]">◆</span> Miss tasks → a proportional share of your {stake} credits moves to {partnerName}, minus fee.</li>
          <li className="flex gap-2"><span className="text-[#F59E0B]">◆</span> Your partner's misses flow to you the same way.</li>
        </ul>
      </div>

      <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="sprint-ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1 accent-[#F59E0B]" />
        <span className="text-sm text-[#F5F5F0] font-light">
          I understand. Lock {stake} credits into stakes for {lengthDays} days.
        </span>
      </label>

      <button
        onClick={() => onStart({ lengthDays, stakeTokens: stake })}
        disabled={!ack}
        data-testid="sprint-start"
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold ${ack ? "hm-btn-primary" : "bg-white/10 text-[#7a7a80] cursor-not-allowed"}`}
      >
        <HandCoins className="w-4 h-4" /> Lock in the stakes
      </button>
    </div>
  );
}
function FieldCard({ label, children }) {
  return (
    <div className="p-3 rounded-md border border-white/10 bg-[#0f0f0f]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{label}</div>
      {children}
    </div>
  );
}

/**
 * Live stakes card during a sprint.
 */
export function SprintStakesCard({ settlement, sprint, partnerName }) {
  if (!settlement || !sprint) return null;
  return (
    <div className="hm-card p-6 rounded-md" data-testid="sprint-stakes-card">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
        <div className="overline">Stakes · live</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <StakeSide label="You" stake={settlement.stake} keep={settlement.myKeep} receive={settlement.myReceive} net={settlement.myNet} missed={settlement.myMissed} scheduled={settlement.myScheduled} breakdown={settlement.myTaskBreakdown} />
        <StakeSide label={partnerName} stake={settlement.stake} keep={settlement.themKeep} receive={settlement.themReceive} net={settlement.themNet} missed={settlement.themMissed} scheduled={settlement.themScheduled} breakdown={settlement.themTaskBreakdown} />
      </div>
      <div className="text-[10px] text-[#7a7a80] text-right">Platform fee so far: {settlement.platformFee} credits</div>
    </div>
  );
}
function StakeSide({ label, stake, keep, receive, net, missed, scheduled, breakdown = [] }) {
  const pct = scheduled > 0 ? Math.round(((scheduled - missed) / scheduled) * 100) : 100;
  return (
    <div className="p-4 rounded-md border border-white/10 bg-[#0f0f0f]">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">{label}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F59E0B]">{pct}% on track</div>
      </div>
      <div className="font-serif-editorial text-3xl mb-1">{net} credits</div>
      <div className="text-[10px] text-[#7a7a80] uppercase tracking-[0.18em]">Net at this moment</div>
      <div className="mt-3 text-xs text-[#A1A1AA] space-y-0.5">
        <div>Keeps: <span className="text-[#F5F5F0]">{keep}</span> of {stake} credits</div>
        <div>Receives: <span className="text-[#F5F5F0]">{receive} credits</span></div>
        <div>Missed: {missed} / {scheduled} scheduled</div>
      </div>
      {breakdown.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
          {breakdown.map((task) => (
            <div key={task.taskId} className="flex justify-between gap-3 text-[10px] text-[#7a7a80]">
              <span className="truncate">{task.taskText}</span>
              <span className={task.chargedTokens > 0 ? "text-[#F59E0B] shrink-0" : "shrink-0"}>
                {task.missed} missed · -{task.chargedTokens}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Ended state panel.
 */
export function EndedPanel({ partnership, partnerName }) {
  const reason = partnership.endReason;
  const ex = partnership.explorationDecision || {};
  const message = reason === "you_declined" ? ex.myMessage : ex.theirMessage;
  return (
    <div className="hm-card p-6 rounded-md mb-6 border border-white/5" data-testid="ended-panel">
      <div className="overline mb-2">Partnership ended</div>
      <h2 className="font-serif-editorial text-2xl leading-tight mb-3">
        {reason === "you_declined" && <>You ended it with {partnerName}.</>}
        {reason === "partner_declined" && <>{partnerName} decided not to continue.</>}
        {reason === "exploration_no" && <>The partnership ended after the Starter Sprint.</>}
        {!reason && <>This partnership has ended.</>}
      </h2>
      {message && (
        <div className="p-4 rounded-md border border-white/5 bg-white/[0.02] text-sm text-[#A1A1AA] font-light italic mb-3">
          "{message}"
        </div>
      )}
      <p className="text-xs text-[#7a7a80] font-light">Ending after the Starter Sprint does not restore either person's match credit.</p>
    </div>
  );
}

/**
 * Sprint end / settlement panel (final numbers).
 */
export function SprintSettlementPanel({ partnership, partnerName, onNewSprint }) {
  const s = partnership.sprint;
  if (!s) return null;
  const st = partnership.__liveSettlement || {};
  return (
    <div className="hm-card p-6 sm:p-8 rounded-md mb-6 border border-emerald-500/30" data-testid="sprint-settlement-panel">
      <div className="overline mb-2">Sprint {s.number} · complete</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-tight mb-3">
        You made it {s.lengthDays} days.
      </h2>
      <p className="text-sm text-[#A1A1AA] font-light max-w-2xl mb-6">
        Here's how the stakes settle. If you and {partnerName} want to keep going, propose a new sprint together — you set the length and the amount this time.
      </p>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <StakeSide label="You" stake={st.stake || s.stakeTokens} keep={st.myKeep} receive={st.myReceive} net={st.myNet} missed={st.myMissed} scheduled={st.myScheduled} breakdown={st.myTaskBreakdown} />
        <StakeSide label={partnerName} stake={st.stake || s.stakeTokens} keep={st.themKeep} receive={st.themReceive} net={st.themNet} missed={st.themMissed} scheduled={st.themScheduled} breakdown={st.themTaskBreakdown} />
      </div>
      <div className="text-xs text-[#7a7a80] mb-6">Platform fee: {st.platformFee || 0} credits</div>
      <button onClick={onNewSprint} data-testid="new-sprint-btn" className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
        <ArrowRight className="w-4 h-4" /> Set up the next Sprint Pact
      </button>
    </div>
  );
}
